'use client';

import Link from 'next/link';
import Image from 'next/image';
import { Collection, Product } from '@/lib/shopify/types';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/dynamic-navigation-menu';
import { getMenuSections } from './shadcn-mobile-mega-menu';

interface ServiceItem {
  title: string;
  path: string;
  image: string;
}

interface ShadcnMegaMenuProps {
  collections: Collection[];
  featuredItems: Product[];
  services: { title: string; path: string }[];
}

export default function ShadcnMegaMenu({ collections, featuredItems, services }: ShadcnMegaMenuProps) {
  // Get menu sections from shared configuration
  const menuSections = getMenuSections(collections, featuredItems, services);
  // Get service items from the menu sections with type assertion
  const serviceItems = menuSections.find(section => section.id === 'services')?.items as ServiceItem[] || [];

  return (
    <NavigationMenu className="hidden md:flex">
      <NavigationMenuList className="gap-1 lg:gap-3">
        {/* Catalog Menu Item */}
        <NavigationMenuItem>
          <NavigationMenuTrigger className="text-sm lg:text-base text-white hover:text-[#F1EFEC]">
            Shop
          </NavigationMenuTrigger>
          <NavigationMenuContent >
          <div className="p-4 md:w-[400px] lg:w-[500px] ">
          <div className="mb-6 flex items-center justify-between">
                <h3 className="px-4 text-base font-semibold text-white">Browse Categories</h3>
              </div>

              {/* Featured Collections */}
              <div className="mb-6">
                <h4 className="mb-3 px-4 text-sm font-medium text-neutral-300">Featured Collections</h4>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 lg:grid-cols-3">
                  {collections
                    .filter(collection => !collection.handle.startsWith('hidden-'))
                    .slice(0, 9)
                    .map((collection) => (
                    <Link
                      key={collection.handle}
                      href={collection.path}
                      prefetch={true}
                      className="block px-4 py-2 text-sm text-white hover:bg-neutral-700 hover:text-[#F1EFEC] rounded-md truncate"
                    >
                      {collection.title}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Featured Products */}
              <div>
                <h4 className="mb-3 px-4 text-sm font-medium text-neutral-300">Featured Products</h4>
                <div className="grid grid-cols-3 gap-3">
                  {Array.isArray(featuredItems) && featuredItems
                    .filter(product => !product.handle.startsWith('hidden-'))
                    .slice(0, 3)
                    .map((product) => (
                    <Link
                      key={product.handle}
                      href={`/product/${product.handle}`}
                      prefetch={true}
                      className="block overflow-hidden rounded-lg border border-neutral-700 transition-all hover:border-[#F1EFEC]"
                    >
                      {product.featuredImage && (
                        <div className="relative h-36 w-full overflow-hidden">
                          <Image
                            src={product.featuredImage.url}
                            alt={product.title}
                            fill
                            sizes="(max-width: 768px) 100vw, 33vw"
                            className="object-cover transition-transform duration-300 hover:scale-105"
                          />
                        </div>
                      )}
                      <div className="p-3">
                        <p className="text-sm font-medium text-white truncate">{product.title}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>

        {/* Services Menu Item */}
        <NavigationMenuItem>
          <NavigationMenuTrigger className="text-sm lg:text-base text-white hover:text-[#F1EFEC]">
            Services
          </NavigationMenuTrigger>
          <NavigationMenuContent >
          <div className="p-4 md:w-[400px] lg:w-[500px] ">
              <h3 className="mb-4 px-4 text-base font-semibold text-white">Our Services</h3>
              <div className="grid grid-cols-2 gap-3 px-4">
                {serviceItems.map((service) => (
                  <Link
                    key={service.title}
                    href={service.path}
                    prefetch={true}
                    className="block overflow-hidden rounded-lg border border-neutral-700 transition-all hover:border-[#F1EFEC]"
                  >
                    <div className="relative h-24 w-full overflow-hidden">
                      <Image
                        src={service.image || '/images/placeholder.png'}
                        alt={service.title}
                        fill
                        sizes="(max-width: 768px) 200px, 80px"
                        className="object-cover transition-transform duration-300 hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end">
                        <div className="p-2 w-full text-white">
                          <h4 className="font-medium text-sm">{service.title}</h4>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>

        {/* Featured Menu Item */}
        <NavigationMenuItem>
          <NavigationMenuTrigger className="text-sm lg:text-base text-white hover:text-[#F1EFEC]">
            Featured
          </NavigationMenuTrigger>
          <NavigationMenuContent >
          <div className="p-4 md:w-[400px] lg:w-[500px] ">
              <h3 className="mb-4 px-4 text-base font-semibold text-white">Featured Items</h3>
              <div className="grid grid-cols-2 gap-4">
                {Array.isArray(featuredItems) && featuredItems
                  .filter(product => !product.handle.startsWith('hidden-'))
                  .slice(0, 4)
                  .map((product) => (
                  <Link
                    key={product.handle}
                    href={`/product/${product.handle}`}
                    prefetch={true}
                    className="block overflow-hidden rounded-lg border border-neutral-700 transition-all hover:border-[#F1EFEC]"
                  >
                    {product.featuredImage && (
                      <div className="relative h-36 w-full overflow-hidden">
                        <Image
                          src={product.featuredImage.url}
                          alt={product.title}
                          fill
                          sizes="(max-width: 768px) 50vw, 33vw"
                          className="object-cover transition-transform duration-300 hover:scale-105"
                        />
                      </div>
                    )}
                    <div className="p-3">
                      <p className="text-sm font-medium text-white truncate">{product.title}</p>
                      <p className="text-xs text-neutral-300">
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: product.priceRange.minVariantPrice.currencyCode
                        }).format(parseFloat(product.priceRange.minVariantPrice.amount))}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
              <div className="mt-4 border-t border-neutral-200 pt-3 dark:border-neutral-700">
                <Link
                  href="/search"
                  prefetch={true}
                  className="block px-4 py-2 text-base font-bold text-neutral-600 hover:text-black dark:text-neutral-400 dark:hover:text-white"
                >
                  View All Featured Items →
                </Link>
              </div>
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>
  );
}
